/**
 * RoyaleEndScreen
 *
 * The persistent end-of-race banner shown ABOVE the (final) board once a Puzzle
 * Royale ends, and stays after the result modal is dismissed (web does NOT
 * navigate away on end). Compact + polished: a teal header strip with a trophy,
 * "Puzzle Royale ended", and your placement chip; a row of stat pills (rank /
 * points / solved / best combo); and a tidy action row (host Rematch +
 * View results + Exit). Presentational — parent passes my final row.
 */

import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import CCText from '@/Atoms/CCText';
import {FontSizes} from '@/Utils/Dimensions';
import IconTooltipButton from '@/Screens/Classroom/components/IconTooltipButton';

import type {PlayerRoyaleState} from '@/Screens/FunChallenges/royaleTypes';

/** Accuracy = solved / (solved + wrong), rounded %. 0 attempts → null (shows —). */
function accuracyPct(solved: number, wrong: number): number | null {
  const total = solved + wrong;
  if (total <= 0) {
    return null;
  }
  return Math.round((solved / total) * 100);
}

const TEAL = '#3DAB9E';
const TEAL_DARK = '#2E8B7F';
const BROWN = '#4D3F37';
const TEXT_MUTED = '#8A7E5F';
const CREAM3 = '#EDE4CC';
const PILL_BG = '#F6F1E4';

interface Props {
  me?: PlayerRoyaleState | null;
  myRank?: number;
  totalPlayers: number;
  /**
   * SOLO Puzzle Royale (1-player). Drops the placement/"you won" framing (there
   * are no opponents to beat) — the end card is just "Time's up!" + your stats.
   */
  solo?: boolean;
  /** Host-only: show the Rematch/Play-again button (server pushes a new lobby). */
  canRematch?: boolean;
  onRematch?: () => void;
  /** Reopen the full result modal (standings). Hidden in solo (no standings). */
  onViewResults?: () => void;
  /** Leave the battle screen (player → Fun Challenges; spectator → stop). */
  onExit?: () => void;
  /** Top-right close (X): dismiss the on-board overlay → reveals the result card
   *  below the board + unlocks completed-puzzle review. Omit → no X. */
  onClose?: () => void;
  /** When set (e.g. spectators, who have no `me`), render inline standings with
   *  points + best combo per player instead of a lone header (#3). */
  players?: Record<string, PlayerRoyaleState>;
  leaderboard?: string[];
  /**
   * COMPACT layout for the below-board slot (replacing the hint): a single tidy
   * row — title + inline stat chips + Play-again/Exit — so it fits the space
   * instead of the tall overlay card. Used post-dismiss; the overlay itself
   * stays full-size.
   */
  compact?: boolean;
}

export default function RoyaleEndScreen({
  me,
  myRank,
  totalPlayers,
  solo = false,
  canRematch,
  onRematch,
  onViewResults,
  onExit,
  onClose,
  players,
  leaderboard,
  compact = false,
}: Props) {
  const placed = me && myRank != null;
  const won = placed && myRank === 0;
  const showStandings = !!players && !!leaderboard && leaderboard.length > 0;
  const acc = me ? accuracyPct(me.solved, me.wrong) : null;

  // Compact below-board strip: ONE line — inline stat chips + actions (no
  // "Time's up!" title). Play = green TEXT button; View results = neutral podium
  // icon; Exit = red exit-to-app icon with a hover tooltip. Fits the hint slot.
  if (compact && me) {
    return (
      <View style={styles.compactCard}>
        <View style={styles.compactStats}>
          <CompactStat icon="star-four-points" label="Points" value={String(me.points)} />
          <CompactStat icon="check-decagram" label="Solved" value={String(me.solved)} />
          <CompactStat icon="lightning-bolt" label="Streak" value={`x${me.bestCombo}`} />
          <CompactStat
            icon="bullseye-arrow"
            label="Accuracy"
            value={acc == null ? '—' : `${acc}%`}
          />
        </View>
        <View style={styles.compactActions}>
          {canRematch && (
            <Pressable
              onPress={onRematch}
              style={styles.playBtn}
              accessibilityLabel={solo ? 'Play again' : 'Rematch'}>
              <CCText style={styles.playBtnText}>Play</CCText>
            </Pressable>
          )}
          {!solo && onViewResults && (
            <IconTooltipButton
              icon="podium"
              tooltip="Results"
              onPress={onViewResults}
              iconColor={BROWN}
              iconSize={19}
              style={[styles.iconBtn, styles.iconBtnNeutral]}
            />
          )}
          {onExit && (
            <IconTooltipButton
              icon="exit-to-app"
              tooltip="Exit"
              onPress={onExit}
              iconColor="#fff"
              iconSize={19}
              style={[styles.iconBtn, styles.iconBtnExit]}
            />
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.block}>
      {/* Teal header strip */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          {/* Icon, not emoji: a trophy when you won (multiplayer #1), else a
              checkered finish flag. */}
          <MaterialCommunityIcons
            name={won ? 'trophy' : 'flag-checkered'}
            size={26}
            color="#fff"
            style={styles.trophy}
          />
          <View>
            <CCText style={styles.title}>
              {solo ? "Time's up!" : 'Puzzle Royale ended'}
            </CCText>
            {/* Solo: no placement line (no opponents). Multiplayer: rank/won. */}
            {!solo && placed && (
              <CCText style={styles.subtitle}>
                {won ? 'You won!' : `You finished #${myRank! + 1}`}
              </CCText>
            )}
            {solo && me && (
              <CCText style={styles.subtitle}>
                {me.solved} solved · {me.points} points
              </CCText>
            )}
          </View>
        </View>
        <View style={styles.headerRight}>
          {/* Rank chip is meaningless for a 1-player game — hide it for solo. */}
          {!solo && placed && (
            <View style={styles.rankChip}>
              <CCText style={styles.rankChipText}>
                #{myRank! + 1}
                <CCText style={styles.rankChipTotal}> / {totalPlayers}</CCText>
              </CCText>
            </View>
          )}
          {/* Close (X): dismiss the overlay → result shows below the board and
              completed-puzzle review unlocks. */}
          {onClose && (
            <Pressable
              onPress={onClose}
              hitSlop={8}
              style={styles.closeBtn}
              accessibilityLabel="Close">
              <MaterialCommunityIcons name="close" size={18} color="#fff" />
            </Pressable>
          )}
        </View>
      </View>

      {/* Stat pills — players only (spectators read standings on the rail).
          Icons match the live stats header (points/solved/streak) + a target for
          accuracy. */}
      {me && (
        <View style={styles.pills}>
          <StatPill icon="star-four-points" label="Points" value={String(me.points)} />
          <StatPill icon="check-decagram" label="Solved" value={String(me.solved)} />
          <StatPill icon="lightning-bolt" label="Best streak" value={`x${me.bestCombo}`} />
          <StatPill
            icon="bullseye-arrow"
            label="Accuracy"
            value={acc == null ? '—' : `${acc}%`}
          />
        </View>
      )}

      {/* Inline standings — for spectators (no personal stats): winner-first
          list with points + best combo per player (#3). */}
      {showStandings && (
        <View style={styles.standings}>
          {leaderboard!.slice(0, 5).map((id, idx) => {
            const p = players![id];
            if (!p) return null;
            return (
              <View key={id} style={styles.standRow}>
                <View
                  style={[styles.standRank, idx === 0 && styles.standRankGold]}>
                  <CCText
                    style={[
                      styles.standRankText,
                      idx === 0 && styles.standRankTextGold,
                    ]}>
                    {idx + 1}
                  </CCText>
                </View>
                <CCText style={styles.standName} numberOfLines={1}>
                  {p.name || 'Player'}
                </CCText>
                <View style={styles.standCombo}>
                  <MaterialCommunityIcons
                    name="lightning-bolt"
                    size={12}
                    color={TEXT_MUTED}
                  />
                  <CCText style={styles.standComboText}>{p.bestCombo}</CCText>
                </View>
                <CCText style={styles.standPts}>{p.points} pts</CCText>
              </View>
            );
          })}
        </View>
      )}

      {/* Actions. Solo: the primary button is "Play again" (no opponents to
          "rematch"); multiplayer keeps "Rematch". View results is hidden in solo
          (no standings) — the parent omits onViewResults there. */}
      <View style={styles.actions}>
        {canRematch && (
          <Pressable onPress={onRematch} style={styles.rematchBtn}>
            <CCText style={styles.rematchText}>
              {solo ? 'Play again' : 'Rematch'}
            </CCText>
          </Pressable>
        )}
        {!solo && onViewResults && (
          <Pressable onPress={onViewResults} style={styles.secondaryBtn}>
            <CCText style={styles.secondaryText}>View results</CCText>
          </Pressable>
        )}
        {onExit && (
          <Pressable onPress={onExit} style={styles.secondaryBtn}>
            <CCText style={styles.secondaryText}>Exit</CCText>
          </Pressable>
        )}
      </View>
      {/* Multiplayer-only hint (a fresh race everyone joins). Meaningless in
          solo, so it's suppressed there. */}
      {canRematch && !solo && (
        <CCText style={styles.rematchHint}>
          Rematch starts a fresh race with the same settings — everyone here
          joins automatically.
        </CCText>
      )}
    </View>
  );
}

function StatPill({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <View style={styles.pill}>
      <CCText style={styles.pillValue}>{value}</CCText>
      <View style={styles.pillLabelRow}>
        <MaterialCommunityIcons name={icon} size={11} color={TEXT_MUTED} />
        <CCText style={styles.pillLabel}>{label}</CCText>
      </View>
    </View>
  );
}

function CompactStat({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <View style={styles.compactStat}>
      <CCText style={styles.compactStatValue}>{value}</CCText>
      <View style={styles.compactStatLabelRow}>
        <MaterialCommunityIcons name={icon} size={9} color={TEXT_MUTED} />
        <CCText style={styles.compactStatLabel}>{label}</CCText>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  block: {
    backgroundColor: '#fff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: CREAM3,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 10,
    shadowOffset: {width: 0, height: 3},
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: TEAL,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  headerLeft: {flexDirection: 'row', alignItems: 'center', gap: 12, flexShrink: 1},
  headerRight: {flexDirection: 'row', alignItems: 'center', gap: 8},
  closeBtn: {
    width: 26,
    height: 26,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.22)',
  },
  trophy: {marginRight: 2},
  title: {fontSize: FontSizes[18] || 18, fontWeight: '800', color: '#fff'},
  subtitle: {
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: 'rgba(255,255,255,0.9)',
    marginTop: 1,
  },
  rankChip: {
    backgroundColor: TEAL_DARK,
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 7,
  },
  rankChipText: {fontSize: FontSizes[16] || 16, fontWeight: '800', color: '#fff'},
  rankChipTotal: {fontSize: FontSizes[12] || 12, fontWeight: '700', color: 'rgba(255,255,255,0.8)'},

  pills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    paddingHorizontal: 16,
    paddingTop: 14,
  },
  pill: {
    // 4 pills: 2×2 on narrow, one row when wide. flexBasis keeps them even.
    flexGrow: 1,
    flexBasis: '20%',
    minWidth: 68,
    backgroundColor: PILL_BG,
    borderRadius: 12,
    paddingVertical: 10,
    alignItems: 'center',
  },
  pillValue: {fontSize: FontSizes[20] || 20, fontWeight: '800', color: BROWN},
  pillLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    marginTop: 2,
  },
  pillLabel: {
    fontSize: FontSizes[10] || 10,
    fontWeight: '700',
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },

  standings: {paddingHorizontal: 16, paddingTop: 14, gap: 6},
  standRow: {flexDirection: 'row', alignItems: 'center', gap: 10},
  standRank: {
    width: 22,
    height: 22,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CREAM3,
  },
  standRankGold: {backgroundColor: '#E7B325'},
  standRankText: {fontSize: FontSizes[12] || 12, fontWeight: '800', color: BROWN},
  standRankTextGold: {color: '#fff'},
  standName: {flex: 1, fontSize: FontSizes[14] || 14, fontWeight: '700', color: BROWN},
  standCombo: {flexDirection: 'row', alignItems: 'center', gap: 2},
  standComboText: {fontSize: FontSizes[12] || 12, fontWeight: '700', color: TEXT_MUTED},
  standPts: {fontSize: FontSizes[14] || 14, fontWeight: '800', color: TEAL},
  actions: {flexDirection: 'row', gap: 10, paddingHorizontal: 16, paddingTop: 14, paddingBottom: 6},
  rematchBtn: {
    flex: 1,
    backgroundColor: TEAL,
    borderRadius: 999,
    paddingVertical: 11,
    alignItems: 'center',
  },
  rematchText: {fontSize: FontSizes[14] || 14, fontWeight: '800', color: '#fff'},
  secondaryBtn: {
    flex: 1,
    backgroundColor: PILL_BG,
    borderRadius: 999,
    paddingVertical: 11,
    alignItems: 'center',
  },
  secondaryText: {fontSize: FontSizes[13] || 13, fontWeight: '800', color: BROWN},
  rematchHint: {
    fontSize: FontSizes[12] || 12,
    color: TEXT_MUTED,
    paddingHorizontal: 16,
    paddingBottom: 14,
  },

  // ── Compact below-board strip: single row — stats (left) + icon actions (right).
  compactCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: CREAM3,
    paddingVertical: 8,
    paddingHorizontal: 14,
    gap: 12,
  },
  compactStats: {
    flexDirection: 'row',
    flex: 1,
    gap: 16,
    flexWrap: 'wrap',
  },
  compactStat: {alignItems: 'center', minWidth: 44},
  compactStatValue: {fontSize: FontSizes[16] || 16, fontWeight: '800', color: BROWN},
  compactStatLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  compactStatLabel: {
    fontSize: FontSizes[9] || 9,
    fontWeight: '700',
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  compactActions: {flexDirection: 'row', gap: 8, alignItems: 'center'},
  playBtn: {
    backgroundColor: TEAL,
    borderRadius: 999,
    paddingHorizontal: 20,
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
  },
  playBtnText: {fontSize: FontSizes[14] || 14, fontWeight: '800', color: '#fff'},
  iconBtn: {
    width: 38,
    height: 38,
    borderRadius: 999,
  },
  iconBtnExit: {backgroundColor: '#CD2C39'},
  iconBtnNeutral: {
    backgroundColor: PILL_BG,
    borderWidth: 1,
    borderColor: CREAM3,
  },
});
